/**
 * FZ-4110 (T600) — ESQUELETO do adaptador realtime da jornada.
 *
 * Responsabilidade única: traduzir os eventos do Pusher para o que a UI precisa,
 * separando as DUAS finalidades (exigência de AC3):
 *
 *  - DADO: `TicketChanged` sinaliza que o bilhete mudou → chama
 *    `JourneyService.refetch()`. Quem coalesce as rajadas é o JourneyService
 *    (janela de 1 s, `REFETCH_WINDOW_MS`); aqui TODO evento chama refetch.
 *  - EFEITO: os eventos específicos (chamado, iniciado, concluído, cancelado,
 *    reaberto, feedback, previsão) NÃO buscam dado — publicam em `effects$`
 *    para som/toast/animação. A mesma dupla kind+bilhete em <1 s é entrega
 *    duplicada (o backend publica o evento em mais de um canal) e vira UMA
 *    emissão.
 *
 * Assina somente `ticket-{id}`: os canais `serviceConfig-{id}` /
 * `service-config-{id}` não fazem parte do contrato público da jornada.
 * Nenhum cache de payload (ADR-001) — o único estado aqui é o dedup de efeitos.
 *
 * T200/T301 evoluem a apresentação; este arquivo é o adaptador realtime do
 * T600 e pode ser reaproveitado sem mudança de contrato.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PusherService } from 'src/app/shared/services/pusher.service';
import { JourneyService } from './journey.service';
import { JourneyConnectionState } from './models/journey.dto';

/** Efeito colateral (sem dado) que a UI consome: som, toast, animação. */
export type JourneyEffectKind =
  | 'called' | 'started' | 'completed' | 'canceled' | 'reopened' | 'feedback' | 'prevision';

/** Um efeito publicado em `effects$`, sempre associado ao bilhete de origem. */
export interface JourneyEffect {
  kind: JourneyEffectKind;
  ticketId: number;
}

/** Estados do Pusher que a UI apresenta como "Reconectando". */
const RECONNECTING_STATES = ['unavailable', 'failed', 'disconnected'];

@Injectable()
export class JourneyRealtimeService implements OnDestroy {
  /** Janela em que entregas repetidas do mesmo kind+bilhete viram uma só emissão. */
  static readonly EFFECT_DEDUP_WINDOW_MS = 1000;

  /**
   * Eventos de EFEITO do contrato, mapeados para o kind consumido pela UI.
   * `TicketChanged` NÃO entra aqui: é evento de DADO (ver `subscribeTicket`).
   */
  private static readonly EFFECT_EVENTS: Array<[string, JourneyEffectKind]> = [
    ['TicketCalled', 'called'],
    ['TicketStart', 'started'],
    ['TicketComplete', 'completed'],
    ['TicketCancel', 'canceled'],
    ['TicketReopened', 'reopened'],
    ['TicketFeedback', 'feedback'],
    ['PrevisionChanged', 'prevision']
  ];

  private readonly effectsSubject = new Subject<JourneyEffect>();
  /** Somente efeitos: som/toast/animação (nunca dado da jornada). */
  readonly effects$: Observable<JourneyEffect> = this.effectsSubject.asObservable();

  private readonly connectionStateSubject = new BehaviorSubject<JourneyConnectionState>('connected');
  /** Estado da conexão realtime; inicial `connected` (a tela já carrega no init). */
  readonly connectionState$: Observable<JourneyConnectionState> = this.connectionStateSubject.asObservable();

  /** Disposers das assinaturas ativas, na ordem de aquisição. */
  private disposers: Array<() => void> = [];

  /** Dedup de efeitos: `${kind}:${ticketId}` → instante do último aceite. */
  private readonly lastEffectAt = new Map<string, number>();

  /** Conexão do Pusher; guardada para soltar o bind no destroy. */
  private readonly connection: any;

  /** Handler único de `state_change` (bind no construtor, sem refetch — quem chama é o componente). */
  private readonly onConnectionStateChange = (state: { current?: string }): void => {
    const current = state ? state.current : undefined;

    if (current === 'connected') {
      this.publishConnectionState('connected');
      return;
    }

    if (RECONNECTING_STATES.indexOf(current) >= 0) {
      this.publishConnectionState('reconnecting');
    }
  };

  constructor(private pusher: PusherService, private journey: JourneyService) {
    // O aviso "Reconectando" vale mesmo antes de `start()`: o bind da conexão é
    // único e vive no construtor, independente das assinaturas de canal.
    this.connection = this.pusher && this.pusher.pusher ? this.pusher.pusher.connection : null;

    if (this.connection && typeof this.connection.bind === 'function') {
      this.connection.bind('state_change', this.onConnectionStateChange);
    }
  }

  /**
   * Assina os canais da jornada para os bilhetes informados.
   *
   * `ticketIds` é a união de `stages[].tickets[].ticketId` (o componente monta a
   * lista e a chama de novo quando os ids mudam). Ids repetidos assinam UMA vez.
   * Chamar novamente derruba as assinaturas anteriores: nunca há canal duplicado
   * nem órfão. Lista vazia não assina nada.
   */
  start(ticketIds: number[]): void {
    this.stop();

    const uniqueIds = this.uniqueTicketIds(ticketIds);
    if (uniqueIds.length === 0) return;

    uniqueIds.forEach((ticketId) => this.subscribeTicket(ticketId));
  }

  /** Derruba todas as assinaturas. Idempotente: chamar de novo não faz nada. */
  stop(): void {
    const disposers = this.disposers;
    this.disposers = [];

    disposers.forEach((dispose) => dispose());

    // Sessão encerrada: a janela de dedup não deve atravessar um novo `start`.
    this.lastEffectAt.clear();
  }

  ngOnDestroy(): void {
    this.stop();

    // O serviço pode morrer antes do PusherService: solta o bind para não vazar
    // handler entre instâncias (o fake de outros pacotes pode não ter `unbind`).
    if (this.connection && typeof this.connection.unbind === 'function') {
      this.connection.unbind('state_change', this.onConnectionStateChange);
    }
  }

  /** União única preservando a ordem de chegada (sem NaN/null/undefined). */
  private uniqueTicketIds(ticketIds: number[]): number[] {
    const unique: number[] = [];
    const seen = new Set<number>();

    (ticketIds || []).forEach((ticketId) => {
      if (ticketId === null || ticketId === undefined) return;
      if (seen.has(ticketId)) return;

      seen.add(ticketId);
      unique.push(ticketId);
    });

    return unique;
  }

  /**
   * Assina os OITO eventos do contrato no canal `ticket-{id}` — um `acquire`
   * por evento, cada um comentado com a sua finalidade (exigência de AC3).
   */
  private subscribeTicket(ticketId: number): void {
    const channelName = `ticket-${ticketId}`;

    // ---------------------------------------------------------------------
    // DADO: o bilhete mudou de alguma forma → reconcilia com o servidor.
    // Quem aplica o debounce de 1 s é o JourneyService; aqui é 1:1.
    // ---------------------------------------------------------------------
    this.disposers.push(
      this.pusher.acquire(channelName, 'TicketChanged', () => this.journey.refetch())
    );

    // ---------------------------------------------------------------------
    // EFEITO: eventos específicos para som/toast/animação da jornada.
    // NÃO buscam dado — o payload do Pusher não é fonte de verdade da tela.
    // ---------------------------------------------------------------------
    JourneyRealtimeService.EFFECT_EVENTS.forEach(([eventName, kind]) => {
      this.disposers.push(
        this.pusher.acquire(channelName, eventName, () => this.publishEffect(kind, ticketId))
      );
    });
  }

  /**
   * Publica o efeito, deduplicando entregas do MESMO kind+bilhete em menos de
   * 1 s (o backend emite o evento em mais de um canal). Kinds diferentes ou
   * bilhetes diferentes nunca são coalescidos.
   */
  private publishEffect(kind: JourneyEffectKind, ticketId: number): void {
    const key = `${kind}:${ticketId}`;
    const now = Date.now();
    const last = this.lastEffectAt.get(key);

    if (last !== undefined && now - last < JourneyRealtimeService.EFFECT_DEDUP_WINDOW_MS) {
      return;
    }

    this.lastEffectAt.set(key, now);
    this.effectsSubject.next({ kind, ticketId });
  }

  /** Emite só quando o estado muda, para não re-renderizar a UI sem necessidade. */
  private publishConnectionState(state: JourneyConnectionState): void {
    if (this.connectionStateSubject.value === state) return;
    this.connectionStateSubject.next(state);
  }
}
